/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { getListingVariants } from './getListingVariants';
import { getListingProductIdFromState } from './helpers/entityReadHelpers';
import { upsertListing } from './helpers/entityAdapter';
import { showPublishedToChannelToast } from './helpers/showPublishedToChannelToast';
import handlePublishListingFailures from './helpers/handlePublishListingFailures';
import handlePublishVariantMediaWarnings from './helpers/handlePublishVariantMediaWarnings';
import { showPublishFailedDialog } from '../../components/ProductInfo/PublishFailedDialog';
import l10n from '../../shared/strings';
import refreshListingAssetsForChannels from './helpers/refreshListingAssetsForChannels';
import syncTreeSelectionAfterPublish from './helpers/syncTreeSelectionAfterPublish';
import { setActiveTabByProductChannel, setSelectedChannelByProduct } from './helpers/uiAdapter';

export const publishProductDraft = createAsyncThunk(
  'products/publishProductDraft',
  async ({ productId, channelId, listingVariantIds }, { dispatch, getState, extra: { sdk, Toast } }) => {
    const state = getState();
    const listingProductId = getListingProductIdFromState(state.products, productId, channelId);

    if (!listingProductId) {
      throw new Error('Staging product not found');
    }

    try {
      const publishOptions = listingVariantIds?.length
        ? { variantIds: listingVariantIds, promoteProduct: true }
        : undefined;
      const { data } = await sdk.products.publishListing([listingProductId], publishOptions);

      if (data.failed?.length) {
        handlePublishListingFailures(data.failed, { Toast, showPublishFailedDialog });
        const publishError = new Error(data.failed[0]?.error?.message || 'Publishing failed');
        publishError.publishFailuresHandled = true;
        throw publishError;
      }

      const updatedListing = data.published[0];
      const { user: { team } } = getState();

      showPublishedToChannelToast({
        Toast,
        channelId,
        salesChannels: team?.salesChannels || [],
      });

      handlePublishVariantMediaWarnings(data.published, { showPublishFailedDialog });

      await dispatch(getListingVariants({
        masterProductId: productId,
        channelId,
        listingProductId: updatedListing._id,
      })).unwrap();

      syncTreeSelectionAfterPublish({
        dispatch,
        getState,
        productId,
        channelId,
        listing: updatedListing,
      });

      await refreshListingAssetsForChannels({
        dispatch,
        getState,
        productId,
        listings: [{ channelId, listingProductId: updatedListing._id }],
      });

      return { productId, channelId, updatedListing };
    } catch (error) {
      if (!error.publishFailuresHandled) {
        Toast(l10n.TOAST.PUBLISHING_SERVER_ERROR, {
          type: 'error',
          autoClose: false,
          btnOkValue: l10n.TOAST.PUBLISHING_TRY_AGAIN,
          onOk: () => dispatch(publishProductDraft({ productId, channelId, listingVariantIds })),
        });
      }
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(publishProductDraft.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(publishProductDraft.fulfilled, (state, { payload }) => {
      const { productId, channelId, updatedListing } = payload;
      state.isBusy = false;

      const existingListing = state.entities?.listings?.[updatedListing._id];

      setActiveTabByProductChannel(state, productId, channelId, updatedListing.listingStatus);
      upsertListing(state, {
        ...existingListing,
        ...updatedListing,
        masterProductId: productId,
      });
      setSelectedChannelByProduct(state, productId, channelId);
    })
    .addCase(publishProductDraft.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot publish listing';
    });
};
